import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';

const NAV = [
  { to: '/', label: 'Home' },
  { to: '/come-funziona', label: 'Come funziona' },
  { to: '/prezzi', label: 'Prezzi' },
];

function Header() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header data-testid="marketing-header" className={`sticky top-0 z-40 transition-all ${scrolled ? 'bg-white/80 backdrop-blur-md border-b border-slate-200' : 'bg-white/60 backdrop-blur-sm'}`}>
      <div className="mx-auto max-w-6xl px-4 sm:px-6 h-20 sm:h-24 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="marketing-logo">
          <img src="/brand/klaro-logo.svg" alt="Klaro" className="h-12 sm:h-16 w-auto object-contain" />
        </Link>
        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end className={({ isActive }) => `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? 'text-[#2563EB]' : 'text-slate-700 hover:text-[#2563EB] hover:bg-slate-50'}`}>{n.label}</NavLink>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <Button asChild variant="success" size="sm"><Link to="/dashboard" data-testid="marketing-dashboard-btn">Vai alla dashboard<ArrowRight className="w-4 h-4 ml-1" /></Link></Button>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm"><Link to="/login" data-testid="marketing-login-btn">Accedi</Link></Button>
              <Button asChild variant="success" size="sm"><Link to="/register" data-testid="marketing-register-btn">Registra la tua azienda</Link></Button>
            </>
          )}
        </div>
        <button className="md:hidden touch-target inline-flex items-center justify-center rounded-lg text-slate-800 hover:bg-slate-100" onClick={() => setOpen(!open)} aria-label="Menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white">
          <div className="px-4 py-3 space-y-1">
            {NAV.map((n) => <NavLink key={n.to} to={n.to} end className={({ isActive }) => `block px-3 py-2.5 rounded-lg text-sm font-medium ${isActive ? 'bg-blue-50 text-[#2563EB]' : 'text-slate-700 hover:bg-slate-50'}`}>{n.label}</NavLink>)}
            <div className="pt-2 grid grid-cols-2 gap-2">
              {user ? (
                <Button asChild variant="success" className="col-span-2"><Link to="/dashboard">Vai alla dashboard</Link></Button>
              ) : (
                <>
                  <Button asChild variant="outline"><Link to="/login">Accedi</Link></Button>
                  <Button asChild variant="success"><Link to="/register">Registrati</Link></Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="mt-16 border-t border-slate-200 bg-slate-50">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10 grid gap-8 sm:grid-cols-2 md:grid-cols-3">
        <div>
          <img src="/brand/klaro-logo.png" alt="Klaro" className="h-9 w-auto" />
          <p className="mt-3 text-sm text-slate-600 max-w-xs">La piattaforma HACCP italiana per piccoli laboratori. Digitalizza DDT, lotti, ricette ed etichette in un pomeriggio.</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-3">Prodotto</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/come-funziona" className="text-slate-700 hover:text-[#2563EB]">Come funziona</Link></li>
            <li><Link to="/prezzi" className="text-slate-700 hover:text-[#2563EB]">Prezzi</Link></li>
            <li><Link to="/register" className="text-slate-700 hover:text-[#2563EB]">Registra la tua azienda</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-3">Legale</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/privacy" className="text-slate-700 hover:text-[#2563EB]">Privacy</Link></li>
            <li><Link to="/termini" className="text-slate-700 hover:text-[#2563EB]">Termini</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-200 py-4 text-center text-xs text-slate-500">© {new Date().getFullYear()} Klaro. GDPR · HACCP compliant.</div>
    </footer>
  );
}

export default function MarketingLayout({ children }) {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
